import { Injectable, computed, inject, signal } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceStore } from '../../../../shared/services/workspace.store';
import { FirestoreApi } from '@core/services/data/firestore-api.service';
import type {
  ContractActivitiesDocument
} from '../../../../shared/definitions/contract-activity';
import type {
  ContractActivity,
  ContractActionType,
  ActivityDetails
} from '../models/contract-activity.model';
import type { IContractActivitiesStore } from './contract-activities-store.interface';

/**
 * Firestore implementation of the contract-scoped activities store.
 *
 * Fetches activities on-demand (one-shot `getDoc` of the single
 * `activities/data` document, since activities are append-only and the
 * backend cloud function `logContractActivity` is the sole writer — polling
 * on action suffices).
 *
 * Replaces the legacy `ContractActivitiesService` retired as part of Phase C
 * (2026-04-20).
 */
@Injectable({ providedIn: 'root' })
export class ContractActivitiesFirestoreStore implements IContractActivitiesStore {
  private readonly firestore = inject(Firestore);
  private readonly fs = inject(FirestoreApi);
  private readonly functions = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);

  private readonly _contractUID = signal<string | null>(null);
  private readonly _activities = signal<readonly ContractActivity[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly contractUID = this._contractUID.asReadonly();
  readonly activities = this._activities.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._activities().length);

  async loadForContract(contractUID: string): Promise<void> {
    if (this._contractUID() === contractUID) return;
    this._contractUID.set(contractUID);
    await this.refresh();
  }

  async refresh(): Promise<void> {
    const cid = this._contractUID();
    if (!cid) return;
    this._loading.set(true);
    this._lastError.set(null);
    try {
      const list = await this.fetchActivities(cid);
      this._activities.set(list);
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
    } finally {
      this._loading.set(false);
    }
  }

  async logActivity(
    contractUID: string,
    action: ContractActionType,
    description: string,
    details?: ActivityDetails,
    actionDate?: Date
  ): Promise<string> {
    try {
      const uid = await this.callLogActivity(contractUID, action, description, details, actionDate);
      // Keep the local snapshot in sync if the logged activity belongs to the
      // currently loaded contract — otherwise skip the extra network round-trip.
      if (this._contractUID() === contractUID) {
        await this.refresh();
      }
      return uid;
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async logIndexationNotification(
    contractUID: string,
    indexationType: string,
    baseIndexValue: number,
    newIndexValue: number,
    previousRentAmount: number,
    newRentAmount: number,
    effectiveDate: Date,
    indexationCap?: number,
    notes?: string
  ): Promise<string> {
    const indexationRate = ((newIndexValue - baseIndexValue) / baseIndexValue) * 100;
    const actualIncrease = newRentAmount - previousRentAmount;
    return this.logActivity(
      contractUID,
      'INDEXATION_NOTIFICATION_SENT' as ContractActionType,
      `Indexation notification sent - ${indexationType} (${indexationRate.toFixed(2)}%)`,
      {
        indexationType,
        baseIndexValue,
        newIndexValue,
        indexationRate,
        previousRentAmount,
        newRentAmount,
        effectiveDate,
        indexationCap,
        actualIncrease,
        reason: notes
      }
    );
  }

  async logIndexationApplied(
    contractUID: string,
    indexationType: string,
    indexationRate: number,
    previousRentAmount: number,
    newRentAmount: number,
    effectiveDate: Date
  ): Promise<string> {
    return this.logActivity(
      contractUID,
      'INDEXATION_APPLIED' as ContractActionType,
      `Indexation applied - ${indexationType} (${indexationRate.toFixed(2)}%)`,
      {
        indexationType,
        indexationRate,
        previousRentAmount,
        newRentAmount,
        effectiveDate,
        actualIncrease: newRentAmount - previousRentAmount,
        automaticAction: true
      },
      effectiveDate
    );
  }

  async logTerminationNotification(
    contractUID: string,
    terminationDate: Date,
    reason?: string,
    noticeGiven?: Date
  ): Promise<string> {
    return this.logActivity(
      contractUID,
      'TERMINATION_NOTIFICATION_SENT' as ContractActionType,
      'Termination notification sent',
      {
        terminationDate,
        reason,
        noticeGiven,
        automaticAction: false
      }
    );
  }

  getActivityStats(): Readonly<Record<string, number>> {
    return this._activities().reduce<Record<string, number>>((acc, a) => {
      const key = a.action as string;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }

  getActivityCount(): number {
    return this._activities().length;
  }

  // --- internals ---

  private async fetchActivities(contractUID: string): Promise<ContractActivity[]> {
    const user = this.authStore.user();
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!user?.userUID || !workspaceId) return [];

    const activitiesDocRef = this.fs.doc(
      this.firestore,
      `workspaces/${workspaceId}/contracts/${contractUID}/activities/data`
    );
    try {
      const snap = await this.fs.getDoc(activitiesDocRef);
      if (!snap.exists()) return [];
      const data = snap.data() as ContractActivitiesDocument;
      return (data.activities ?? []).map(activity => ({
        ...activity,
        date: this.convertToDate(activity.date),
        createdAt: this.convertToDate(activity.createdAt),
        details: {
          ...activity.details,
          paymentPeriod: activity.details?.paymentPeriod ? {
            startDate: this.convertToDate(activity.details.paymentPeriod.startDate),
            endDate: this.convertToDate(activity.details.paymentPeriod.endDate)
          } : undefined,
          effectiveDate: activity.details?.effectiveDate
            ? this.convertToDate(activity.details.effectiveDate)
            : undefined,
          terminationDate: activity.details?.terminationDate
            ? this.convertToDate(activity.details.terminationDate)
            : undefined
        }
      } as ContractActivity));
    } catch (err) {
      console.error('Error fetching contract activities:', err);
      return [];
    }
  }

  private async callLogActivity(
    contractUID: string,
    action: ContractActionType,
    description: string,
    details?: ActivityDetails,
    actionDate?: Date
  ): Promise<string> {
    const user = this.authStore.user();
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!user?.userUID) throw new Error('User not authenticated');
    if (!workspaceId) throw new Error('No workspace selected');

    const logActivityFunction = this.fs.httpsCallable(this.functions, 'logContractActivity');
    const result = await logActivityFunction({
      workspaceId,
      contractUID,
      action,
      description,
      details,
      actionDate: actionDate?.toISOString()
    });
    return (result.data as { activityUID: string }).activityUID;
  }

  /**
   * Convert Firestore timestamps, seconds/nanoseconds objects, ISO strings, or
   * epoch numbers back to a JS `Date`. Defensive: used on data coming off the
   * wire where the shape varies.
   */
  private convertToDate(dateValue: unknown): Date {
    if (!dateValue) return new Date();
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === 'string' || typeof dateValue === 'number') return new Date(dateValue);
    if (typeof dateValue === 'object') {
      const v = dateValue as { toDate?: () => Date; seconds?: number };
      if (typeof v.toDate === 'function') return v.toDate();
      if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
    }
    return new Date();
  }
}
